import { type StateCreator } from 'zustand';

import { type PendingSkillInstruction } from '../../types/pendingSkillInstruction';
import { DEFAULT_VARIANT_COUNT, type VariantCount } from '../../types/subscriptions';
import { type MentionItem } from '../../ui/components/ChatComposer/mentions';

export type ChatComposerSlice = {
  /** Registered by the ChatComposer on mount; called to convert all draft tokens to committed. */
  commitTokens: (() => void) | null;
  /** Registered by the ChatComposer on mount; called to programmatically insert a token. */
  insertToken: ((item: MentionItem) => void) | null;
  /** Registered by the ChatComposer on mount; inserts a `/slashCommand ` skill at the caret. */
  /** Registered by the ChatComposer on mount; true while the composer holds unsent user input. */
  hasDraftText: (() => boolean) | null;
  /** Registered by the ChatComposer on mount; moves the caret to the end of the document. */
  moveCaretToEnd: (() => void) | null;
  insertSkill: ((slashCommand: string) => void) | null;
  /** Registered by the ChatComposer on mount; removes a token by document position (one chip) or by ID (all matches). */
  removeToken: ((target: string | number) => void) | null;
  /** Registered by the ChatComposer on mount; opens the @ picker to swap a composer chip at a document position. */
  openTokenSwapPicker: ((tokenPos: number, getRect: () => DOMRect | null) => void) | null;
  /** Registered by the ChatComposer on mount; replaces selection-managed tokens with the given set. */
  syncTokens: ((items: MentionItem[]) => void) | null;
  /** Registered by the ChatComposer on mount; replaces all text in the editor (keeping tokens) without submitting. */
  messageText: ((text: string) => void) | null;
  /** Registered by the ChatComposer on mount; inserts at the cursor without clearing, so callers must stage tokens before the text. */
  appendMessageText: ((text: string) => void) | null;
  /** Registered by the ChatComposer on mount; submits the current composer contents. */
  submitMessage: (() => void) | null;
  /** True while files are being uploaded or a null-state skill starter is being staged via Try it. */
  isUploadingToChat: boolean;
  /** Cleared by AgentProvider after sendMessage; keyed by slash command so re-selecting replaces stale copy. */
  pendingSkillInstructions: PendingSkillInstruction[];
  /** Queued @-mention set before the ChatComposer mounts; drained on mount. */
  pendingToken: MentionItem | null;
  /** Queued plain text set before the ChatComposer mounts; drained on mount. */
  pendingMessageText: string | null;
  /**
   * Local canvas node id of a single grouped layer that a Prompt was initiated
   * against (via the layer toolbar). Consumed and cleared on the next send so
   * the message carries it as the in-place edit target, keeping the result in
   * the group. Null for non-layer prompts.
   */
  promptSourceNodeId: string | null;
  setPromptSourceNodeId: (id: string | null) => void;
  setAppendMessageText: (fn: ((text: string) => void) | null) => void;
  setCommitTokens: (fn: (() => void) | null) => void;
  setMessageText: (fn: ((text: string) => void) | null) => void;
  setSubmitMessage: (fn: (() => void) | null) => void;
  setInsertToken: (fn: ((item: MentionItem) => void) | null) => void;
  setHasDraftText: (fn: (() => boolean) | null) => void;
  setMoveCaretToEnd: (fn: (() => void) | null) => void;
  setInsertSkill: (fn: ((slashCommand: string) => void) | null) => void;
  setIsUploadingToChat: (value: boolean) => void;
  addPendingSkillInstructions: (skill: PendingSkillInstruction) => void;
  removePendingSkillInstruction: (slashCommand: string) => void;
  clearPendingSkillInstructions: () => void;
  setPendingToken: (item: MentionItem | null) => void;
  setPendingMessageText: (text: string | null) => void;
  setRemoveToken: (fn: ((target: string | number) => void) | null) => void;
  setOpenTokenSwapPicker: (fn: ((tokenPos: number, getRect: () => DOMRect | null) => void) | null) => void;
  setSyncTokens: (fn: ((items: MentionItem[]) => void) | null) => void;
  composerTokenTypes: string[];
  setComposerTokenTypes: (types: string[]) => void;
  /**
   * Whether the @-mention, / skill, or plain-text skill-word suggestion UI is open in the chat composer.
   * Updated synchronously (so canvas Escape can observe it in capture phase).
   */
  chatMentionSuggestionOpen: boolean;
  chatSlashCommandSuggestionOpen: boolean;
  chatSkillWordSuggestionOpen: boolean;
  setChatMentionSuggestionOpen: (open: boolean) => void;
  setChatSlashCommandSuggestionOpen: (open: boolean) => void;
  setChatSkillWordSuggestionOpen: (open: boolean) => void;
  variantCount: VariantCount;
  setVariantCount: (count: VariantCount) => void;
};

export type CreateChatComposerSlice = () => StateCreator<ChatComposerSlice, [], [], ChatComposerSlice>;

export const createChatComposerSlice: CreateChatComposerSlice = () => (set) => ({
  addPendingSkillInstructions: (skill) =>
    set((state) => {
      const withoutSame = state.pendingSkillInstructions.filter((e) => e.slashCommand !== skill.slashCommand);

      return {
        pendingSkillInstructions: [...withoutSame, skill],
      };
    }),
  appendMessageText: null,
  chatMentionSuggestionOpen: false,
  chatSkillWordSuggestionOpen: false,
  chatSlashCommandSuggestionOpen: false,
  clearPendingSkillInstructions: () => set({ pendingSkillInstructions: [] }),
  commitTokens: null,
  composerTokenTypes: [],
  hasDraftText: null,
  insertSkill: null,
  insertToken: null,
  isUploadingToChat: false,
  messageText: null,
  moveCaretToEnd: null,
  openTokenSwapPicker: null,
  pendingMessageText: null,
  pendingSkillInstructions: [],
  pendingToken: null,
  promptSourceNodeId: null,
  removePendingSkillInstruction: (slashCommand) =>
    set((state) => ({
      pendingSkillInstructions: state.pendingSkillInstructions.filter((entry) => entry.slashCommand !== slashCommand),
    })),
  removeToken: null,
  setAppendMessageText: (fn) => set({ appendMessageText: fn }),
  setChatMentionSuggestionOpen: (open) => set({ chatMentionSuggestionOpen: open }),
  setChatSkillWordSuggestionOpen: (open) => set({ chatSkillWordSuggestionOpen: open }),
  setChatSlashCommandSuggestionOpen: (open) => set({ chatSlashCommandSuggestionOpen: open }),
  setCommitTokens: (fn) => set({ commitTokens: fn }),
  setComposerTokenTypes: (types) => set({ composerTokenTypes: types }),
  setHasDraftText: (fn) => set({ hasDraftText: fn }),
  setInsertSkill: (fn) => set({ insertSkill: fn }),
  setInsertToken: (fn) => set({ insertToken: fn }),
  setIsUploadingToChat: (value) => set({ isUploadingToChat: value }),
  setMessageText: (fn) => set({ messageText: fn }),
  setMoveCaretToEnd: (fn) => set({ moveCaretToEnd: fn }),
  setOpenTokenSwapPicker: (fn) => set({ openTokenSwapPicker: fn }),
  setPendingMessageText: (text) => set({ pendingMessageText: text }),
  setPendingToken: (item) => set({ pendingToken: item }),
  setPromptSourceNodeId: (id) => set({ promptSourceNodeId: id }),
  setRemoveToken: (fn) => set({ removeToken: fn }),
  setSubmitMessage: (fn) => set({ submitMessage: fn }),
  setSyncTokens: (fn) => set({ syncTokens: fn }),
  setVariantCount: (count) => set({ variantCount: count }),
  submitMessage: null,
  syncTokens: null,
  variantCount: DEFAULT_VARIANT_COUNT,
});
